import { tx as translateInterfaceText } from '@/utils/i18n';
import { useLanguage } from '@/hooks/useLanguage';
import React, { useState, useEffect } from 'react';
import { Search } from 'lucide-react';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { PeriodPicker } from '@/components/ui/period-picker';
import { DEFAULT_PERIOD_PRESETS, type PeriodPreset } from '@/utils/period-presets';
import { ReportsFilters as Filters, VideoOption } from './types';

// "Hoje" continua disponível: era o primeiro atalho deste filtro.
const REPORT_PERIOD_PRESETS: PeriodPreset[] = ['today', ...DEFAULT_PERIOD_PRESETS];

interface ReportsFiltersProps {
  filters: Filters;
  onFiltersChange: (filters: Filters) => void;
  recordCount: number;
  videos?: VideoOption[];
  selectedVideoId?: string;
  onSelectVideo?: (videoId: string) => void;
  isLoadingVideos?: boolean;
}

export const ReportsFilters: React.FC<ReportsFiltersProps> = ({
  filters,
  onFiltersChange,
  recordCount,
  videos = [],
  selectedVideoId,
  onSelectVideo,
  isLoadingVideos,
}) => {
  useLanguage();
  const [searchValue, setSearchValue] = useState(filters.search);

  // Debounce na busca (300ms)
  useEffect(() => {
    const timer = setTimeout(() => {
      onFiltersChange({ ...filters, search: searchValue });
    }, 300);
    return () => clearTimeout(timer);
  }, [searchValue]);

  return (
    <>
      {/* Desktop - Alinhado à esquerda */}
      <div className="hidden lg:flex items-center gap-3">
        {/* 1. Período (Data) - À ESQUERDA */}
        <PeriodPicker
          value={filters.dateRange}
          onChange={(dateRange) => onFiltersChange({ ...filters, dateRange })}
          presets={REPORT_PERIOD_PRESETS}
        />

        {/* 2. Filtro de Vídeo - AO LADO DA DATA */}
        <div className="w-[240px]">
          <Select
            value={selectedVideoId || 'all'}
            onValueChange={(value) => {
              if (onSelectVideo) {
                onSelectVideo(value === 'all' ? '' : value);
              }
            }}
            disabled={isLoadingVideos}
          >
            <SelectTrigger>
              <SelectValue placeholder={translateInterfaceText("Todos os vídeos")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">{translateInterfaceText("Todos os vídeos")}</SelectItem>
              {videos.map((video) => (
                <SelectItem key={video.id} value={video.id}>
                  {video.title}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* 3. Busca - AO LADO DO FILTRO DE VÍDEO */}
        <div className="flex-1">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              type="text"
              placeholder={translateInterfaceText("Buscar por nome, e-mail ou telefone")}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              className="pl-9"
            />
          </div>
        </div>
      </div>

      {/* Mobile */}
      <div className="lg:hidden space-y-3">
        {/* Período - 100% width */}
        <PeriodPicker
          value={filters.dateRange}
          onChange={(dateRange) => onFiltersChange({ ...filters, dateRange })}
          presets={REPORT_PERIOD_PRESETS}
          className="sm:w-full"
        />

        {/* Filtro de Vídeo - 100% width */}
        <Select
          value={selectedVideoId || 'all'}
          onValueChange={(value) => {
            if (onSelectVideo) {
              onSelectVideo(value === 'all' ? '' : value);
            }
          }}
          disabled={isLoadingVideos}
        >
          <SelectTrigger>
            <SelectValue placeholder={translateInterfaceText("Todos os vídeos")} />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">{translateInterfaceText("Todos os vídeos")}</SelectItem>
            {videos.map((video) => (
              <SelectItem key={video.id} value={video.id}>
                {video.title}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Busca - 100% width */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder={translateInterfaceText("Buscar por nome, e-mail ou telefone")}
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="pl-9"
          />
        </div>
      </div>
    </>
  );
};
